import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@components/atoms/Typography';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { SITE_URLS } from '@constants';
import styles from './Notification.module.css';
import { Space } from '@components/atoms/Space';

export interface NotificationProps {
    message: string;
    product?: NotificationProduct | NotificationProduct[];
    size: 'full-width' | 'md' | 'sm';
    type: 'success' | 'warning' | 'error';
    endIcon?: string;
    dismissible?: boolean;
    position?: {
        top: string;
        align?: 'left' | 'center' | 'right';
    };
    autoDismiss?: number; // in seconds
    borderRadiusStyle: 'rounded' | 'not-rounded';
    onClose?: () => void;
    children?: JSX.Element | JSX.Element[];
}

interface NotificationProduct {
    name: string;
    image: string;
    quantity?: number;
}

export const Notification: FC<NotificationProps> = ({
    message,
    product,
    size,
    type,
    endIcon,
    dismissible,
    position,
    autoDismiss,
    borderRadiusStyle,
    onClose,
    children,
}) => {
    const { t } = useTranslation();

    const translations = {
        goToBasket: t('notification.go-to-basket.button'),
    };

    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        if (autoDismiss) {
            setTimeout(() => {
                setIsClosing(true);
            }, autoDismiss * 1000);
        }
    }, [autoDismiss]);

    useEffect(() => {
        if (isClosing && onClose) {
            setTimeout(() => onClose(), 400);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClosing]);

    const [distanceFromWindow, setDistanceFromWindow] = useState(0);

    useEffect(() => {
        const handleResize = () => {
            const distance = (document.querySelector('#header > .container > div') as HTMLDivElement)?.offsetLeft;
            setDistanceFromWindow(distance);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const icon = type === 'success' ? 'fa-circle-check' : type === 'warning' ? 'fa-triangle-exclamation' : 'fa-circle-xmark';

    return (
        <div
            className={
                `${position ? styles['fixed-container'] : ''}` +
                // positioning
                ` ${position ? styles[`fixed-container-${position.align}`] : ''}` +
                // animation
                ` ${isClosing ? styles['slide-out'] : styles['slide-in']}`
            }
            style={
                position
                    ? {
                          top: position.top,
                          right: position.align === 'right' ? `${distanceFromWindow}px` : undefined,
                          left: position.align === 'left' ? `${distanceFromWindow}px` : undefined,
                      }
                    : {}
            }>
            <div
                className={
                    `${styles['alert-container']}` +
                    ` ${size && Array.isArray(product) && product.length > 1 ? styles[`alert-container-bundle-${size}`] : styles[`alert-container-${size}`]}` +
                    ` ${styles[`alert-container-${type}`]}` +
                    // border radius
                    ` ${borderRadiusStyle === 'rounded' ? styles['rounded-border-radius'] : ''}`
                }>
                <div className={`${styles['alert-body']} d-flex justify-content-between gap-3`}>
                    <div className="d-flex align-items-center justify-content-between gap-3 flex-grow-1">
                        <div className="d-flex align-items-center gap-3">
                            <div>
                                <div className={styles['round-icon']}>
                                    <i className={`fa-light ${icon}`} />
                                </div>
                            </div>
                            <div className="d-flex flex-column">
                                <Typography fontSize="16">{message}</Typography>
                                {children}
                            </div>
                        </div>
                        {endIcon && (
                            <Typography fontSize="16">
                                <i className={endIcon} />
                            </Typography>
                        )}
                    </div>
                    {dismissible && (
                        <div>
                            <button type="button" onClick={() => setIsClosing(true)}>
                                <i className="fa-thin fa-xmark" style={{ fontSize: '28px' }}></i>
                            </button>
                        </div>
                    )}
                </div>
                {product && (
                    <>
                        {Array.isArray(product) ? (
                            <div className={styles['product-container-bundle']}>
                                <div className="d-flex align-items-center gap-3">
                                    <img src={product[0].image} alt={product[0].name} />
                                    <div className="d-flex flex-column">
                                        {product.map((prod, index) => (
                                            <div key={index}>
                                                <Typography fontSize="14">
                                                    {prod.quantity && prod.quantity > 1
                                                        ? `${prod.quantity} x ${prod.name}`
                                                        : prod.name}
                                                </Typography>
                                                <Space spaceBreakpoints={{ xs: { space: 15 }, md: { space: 15 } }} />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div onClick={() => setIsClosing(true)}>
                                    <Anchor
                                        href={SITE_URLS.shoppingCart}
                                        mode={AnchorMode.primary}
                                        outline={true}
                                        size="sm"
                                        tall={true}>
                                        {translations.goToBasket}
                                    </Anchor>
                                </div>
                            </div>
                        ) : (
                            <div className={styles['product-container']}>
                                <div className="d-flex align-items-center gap-3">
                                    <img src={product.image} alt={product.name} />
                                    <Typography fontSize="12">
                                        <>
                                            {product.quantity && product.quantity > 1 ? `${product.quantity} x ` : ''}
                                            {product.name}
                                        </>
                                    </Typography>
                                </div>
                                <div onClick={() => setIsClosing(true)}>
                                    <Anchor
                                        href={SITE_URLS.shoppingCart}
                                        mode={AnchorMode.primary}
                                        outline={true}
                                        size="sm"
                                        tall={true}>
                                        {translations.goToBasket}
                                    </Anchor>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
